import { useNeedPay } from 'modules/admin';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as apiUser from 'api/apiUsers';
import { useEffect, useReducer, useState } from 'react';
import UserCardTabs from './UserCardTabs';
import ESpinner from 'components/others/Spinner';
import { useToast } from 'context/ToastContext';
import { SetUserReducer } from './reducer/UserReducer';
import SpinnerBlock from 'components/others/SpinnerBlock';
import type { OffUserCardProps } from '../types';
import type { LeadCloudFileType, TelegramObjectEventType, UserType } from '@econtractor/types';
import { deleteCloudflareFiles, getCloudflareUploadUrls, linkCloudflareFiles } from 'api/apiCloudflare';
import { toStoredCloudFile } from 'modules/files';
import { useRef } from 'react';
import { uploadFileWithProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { fileToWebpFile } from 'utils/helpers/imgFunctions/fileToWebpFile';

const AVATAR_WEBP_QUALITY = 0.8;
const AVATAR_MAX_SIZE = 512;

const OffUserCard = ({ showUserCard, setShowUserCard, fetchUsers, setShowSpinner }: OffUserCardProps) => {
    const { t, i18n } = useTranslation();
    const { showToast } = useToast();
    const { needPay } = useNeedPay();
    const [user, dispatchUser] = useReducer(SetUserReducer, null);
    const [isSaving, setIsSaving] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const originalUserPictureRef = useRef<string>('');

    const prepareUserFromApi = (userData: UserType | null | undefined): UserType | null | undefined => {
        if (!userData) return userData;
        if (userData.telegramEvents) return userData;
        const info = userData.info || {};
        const hasOld =
            info.telegram_lead_statuses ||
            info.telegram_object_events ||
            info.telegram_my_object_events ||
            info.telegram_warehouses;
        if (!hasOld) return userData;
        const { telegram_lead_statuses, telegram_object_events, telegram_my_object_events, telegram_warehouses, ...infoRest } =
            info as Record<string, unknown>;
        return {
            ...userData,
            telegramEvents: {
                leadStatuses: (telegram_lead_statuses as number[]) || [],
                objectEvents: (telegram_object_events as TelegramObjectEventType[]) || [],
                myObjectEvents: (telegram_my_object_events as TelegramObjectEventType[]) || [],
                warehouses: (telegram_warehouses as number[]) || [],
            },
            info: infoRest,
        };
    };

    const fetchUser = async () => {
        if (isFetching || typeof showUserCard !== 'number') return;
        setIsFetching(true);

        const resUser = await apiUser.getUser(showUserCard);
        if (!resUser) showToast(t('messError'), 'danger');
        const userData = prepareUserFromApi(resUser?.user);
        originalUserPictureRef.current = userData?.userPicture || '';
        dispatchUser({ type: 'SET', value: userData ?? null });
        setIsFetching(false);
    };

    const getSavePayload = (sourceUser: any, userPicture: string) => {
        const payload = { ...sourceUser };
        delete payload.__avatar_file;
        return {
            ...payload,
            userPicture: userPicture || '',
            lang: i18n.language,
        };
    };

    const uploadAvatar = async (userId: number, file: File): Promise<LeadCloudFileType | null> => {
        const optimizedFile = await fileToWebpFile(file, AVATAR_WEBP_QUALITY, AVATAR_MAX_SIZE, AVATAR_MAX_SIZE);
        const filesMeta = [
            {
                fileName: optimizedFile.name,
                contentType: optimizedFile.type || 'application/octet-stream',
                fileSize: optimizedFile.size,
            },
        ];
        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, { avatarId: Number(userId || 0) });
        if (!uploadConfigs?.length) return null;
        const uploaded = await uploadFileWithProgress(optimizedFile, uploadConfigs[0]);
        if (!uploaded.success) return null;
        const storedFile = toStoredCloudFile(uploaded.config);
        const linked = await linkCloudflareFiles([storedFile], { avatarId: Number(userId || 0) });
        if (!linked) return null;
        return storedFile;
    }; 

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        if (!Number(user.ID || 0) && needPay()) return;
        setIsSaving(true);
        const userToSave = { ...(user as any) };
        const pendingAvatarFile = userToSave?.__avatar_file as File | undefined;
        const oldPicture = originalUserPictureRef.current || '';

        let targetUserId = Number(userToSave.ID || 0);
        if (targetUserId === 0) {
   
            const resCreate = await apiUser.saveUser(0, getSavePayload(userToSave, ''));
            if (resCreate?.userExists) {
                setIsSaving(false);
                return showToast(t('Users.messUserExists'), 'danger');
            }
            if (!resCreate?.stat || !resCreate?.userId) {
                setIsSaving(false);
                showToast(t('common:messError'), 'danger');
                return;
            }
            targetUserId = Number(resCreate.userId);
        }

        let nextPicture = pendingAvatarFile ? '' : (userToSave?.userPicture || oldPicture || '');
        if (pendingAvatarFile) {
            const uploadedAvatar = await uploadAvatar(targetUserId, pendingAvatarFile);
            if (!uploadedAvatar?.publicUrl) {
                setIsSaving(false);
                showToast(t('common:messError'), 'danger');
                return;
            }
            nextPicture = uploadedAvatar.publicUrl;
        }

        
        const resSaving = await apiUser.saveUser(targetUserId, getSavePayload(userToSave, nextPicture));
        if (resSaving?.userExists) {
            setIsSaving(false);
            return showToast(t('Users.messUserExists'), 'danger');
        }
        if (!resSaving?.stat) {
            setIsSaving(false);
            showToast(t('common:messError'), 'danger');
            return;
        }

        if (pendingAvatarFile && oldPicture && oldPicture !== nextPicture) {
            try {
                await deleteCloudflareFiles({
                    publicUrls: [oldPicture],
                    scope: { avatarId: targetUserId },
                });
            } catch {
                // Не блокуємо збереження / оновлення списку, якщо старий файл не видалився
            }
        }

        await fetchUsers(true);
        setIsSaving(false);
        setShowUserCard(false);
    };

    const handleClose = () => {
        setShowUserCard(false);
        setTimeout(() => {
            dispatchUser({ type: 'SET', value: null });
        }, 300);
    };

    useEffect(() => {
        if (showUserCard && showUserCard !== -1) fetchUser();
        if (showUserCard === -1) {
            originalUserPictureRef.current = '';
            dispatchUser({ type: 'SET', value: { ID: 0, userStatus: 1, access: { role: 'manager' } } });
        }
    }, [showUserCard]);

    return (
        <Offcanvas show={showUserCard !== false} onHide={handleClose} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('Users.titleUserCard')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                {isFetching ? (
                    <ESpinner />
                ) : (
                    <>
                        <form onSubmit={handleSubmit} className="d-flex flex-column h-100">
                            <SpinnerBlock show={isSaving} onlyBlur={true} />
                            {user ? (
                                <UserCardTabs
                                    fetchUsers={fetchUsers}
                                    setShowUserCard={setShowUserCard}
                                    isSaving={isSaving}
                                    user={user}
                                    setShowSpinner={setShowSpinner}
                                    dispatchUser={dispatchUser}
                                />
                            ) : (
                                <ESpinner />
                            )}
                        </form>
                    </>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffUserCard;
